import { isEmpty } from 'lodash'
import Misc from 'src/utils/helper/misc'
import { Toaster } from 'src/utils/helper/toaster'
import { useSubmissionUtils } from 'src/composables/useSubmissionUtils'
import { LINK_NAMES } from 'modules/adobe-cja/constants'

export default {
  setTags() {
    return new Promise(resolve => {
      this.setTagValues()
      resolve()
    })
  },
  setTagValues() {
    /**
     * Set tags values.
     * Filter out AI_GENERATED tags — those are rendered separately via visibleAiTags.
     */
    const userOnlyTags = this.submissionTags.filter(tag => tag.id !== 'AI_GENERATED')
    this.tags.setValues({
      obj: userOnlyTags,
      value: 'id',
      name: 'name',
      multiple: true
    })
  },
  updateTagsValues(val) {
    const values = val || []
    const aiTagCount = this.visibleAiTags?.length || 0
    const combinedCount = values.length + aiTagCount

    if (combinedCount > this.tagsLength) {
      this.maxTagsReached = true
      return
    } else {
      this.maxTagsReached = false
    }

    if (combinedCount === this.tagsLength) {
      this.enforceTagLimit()
    }
    
    this.tags.state = 'set'
    let filteredValues = values.filter(v => {
      return !this.warningWords?.some(word => v.label.toLowerCase() === word.toLowerCase());
    });
    let remainingValues = values.filter(v => {
      return !filteredValues.some(fv => fv.label.toLowerCase() === v.label.toLowerCase());
    })
    filteredValues.push(...remainingValues)
    this.tags.update(filteredValues, this.commitChange)
  },
  removeAllTags() {
    this.clearAllTags()
  },
  deselectFromTag(key) {
    if (!this?.warningWords?.every(word =>
      this.tags.values.some(tag => tag.label.toLowerCase() === word.toLowerCase())
    )) {
      // Filter out words from warningWords that are not present in tags.values
      this.warningWords = this?.warningWords?.filter(word =>
        this.tags.values.some(tag => tag.label.toLowerCase() === word.toLowerCase())
      );
    }
    if (this.getCurrentTagCount() < this.tagsLength) {
      this.maxTagsReached = false
    }
    this.tags.state = 'set'
    this.tags.update(this.tags.values, this.commitChange)
    this.$refs?.tagsSelect?.onSearchBlur()
  },
  tagCountCheck() {
    return (this.tags?.values?.length || 0) + (this.visibleAiTags?.length || 0)
  },
  
  enforceTagLimit() {
    const aiTagCount = this.visibleAiTags?.length || 0
    const userTagCount = this.tags?.values?.length || 0
    if ((userTagCount + aiTagCount) >= this.tagsLength) {
      this.enteredTag = ''
      this.isValidTagLength = false
      this.$emit('update:modelValue', '')
      this.searchCount = 0
      this.maxTagsReached = true
      this.removeErrors()
    }
  },
  tagsDeleteLengthError(search) {
    if (isEmpty(search)) {
      if(this.$refs.tagsSelect)
        this.$refs.tagsSelect._value = this.tags.values
      this.tags.state='set'
      this.isValidTagLength = false
    }
  },
  checkForStopwords(tagName) {
    this.inValidTag = false;
    // Clean and split the tagName into words
    const cleanedTagName = tagName.trim().replace(/\s+/g, ' ');
    const tagWords = cleanedTagName.split(/\s+/);
    const stopWords = this.removeWords.map(word => word.trim().replace(/\s+/g, ' ').toLowerCase());
    // Filter out stop words from the tagWords
    const filteredWords = tagWords.filter(word => !stopWords.includes(word.toLowerCase()));
    // If no words remain after filtering, set inValidTag to true and return false
    if (filteredWords.length === 0) {
      this.inValidTag = true;
      return false;
    }
    if (/^\d{4}$/.test(filteredWords) && (parseInt(filteredWords) < 1800 || parseInt(filteredWords) > 2099)) {
      this.inValidTag = true;
      return false;
    }
      if (/^\d{4}$/.test(filteredWords) && parseInt(filteredWords) >= 1800 && parseInt(filteredWords) <= 2099 && (parseInt(filteredWords) % 10 === 0)) {
        return filteredWords + 's';
      }
    // Return the filtered words joined back into a string
    return filteredWords.join(' ');
  },
  validateTags(tagName, invalidWordsMap) {
    let validTagName;
    Object.entries(invalidWordsMap).forEach(([type, words]) => {
      if (!Array.isArray(words)) {
        words = [words];
      }
      invalidWordsMap[type] = words.map(word => word.trim().replace(/\s+/g, ' ').toLowerCase());
    });
    let cleanedTagName = tagName.trim().replace(/\s+/g, ' ').toLowerCase();
    this.inValidTag = false;

    for (const words of Object.values(invalidWordsMap)) {
      if (words.includes(cleanedTagName)) {
        this.inValidTag = true;
        return false;
      }
    }
    validTagName = this.checkForStopwords(tagName);
    return validTagName;
  },
  updateTags(uniqueArray, currentValues, values, promises, resolve) {
    this.tags.state = 'set';
    let uniqueTags = [...currentValues, ...values];
    const check = currentValues.filter(item =>
      values.some(val => Misc.strToLowerCase(val.label) === Misc.strToLowerCase(item.label))
    );
    if (check.length > 0) {
      uniqueTags = Misc.removeDuplicateObjOnKey(uniqueTags, item => item.label.toLowerCase());
    }
    //This change is added for this bug, https://monotype.atlassian.net/browse/KAI-5948
    //Handles the display warningWord error message if the tag contains specific conditions (tagsError, inValidChar, etc.).
    //A more robust approach is needed in the future for better handling of tags with warning words.
    const currentTags = currentValues.map(v => v.label.toLowerCase()) || []
    if (this.tagsError && this.inValidChar && this.warningWords?.length === 1 && 
      uniqueArray[0].toLowerCase().includes(this.warningWords[0]?.toLowerCase()) && !currentTags.includes(this.warningWords[0]?.toLowerCase())) {
      this.warningWordForTag = false
      this.warningWords = []
    }
    if (!this.tagsError) {
      this.updateTagsValues(uniqueTags);
    }
    this.tagsError = false;
  },
  processTags(tags) {
    const currentValues = this.tags.values || [];
    const values = [];
    const promises = [];
    const uniqueArray = [...new Set(this.alltype.flat())];
    uniqueArray.forEach(tag => {
      const key = values.length;
      const tagName = this.sanitizeTag(tag.trim());
      if (this.isValidTag(tagName, currentValues) && tagName.length > 1) {
        this.addTag(values, key, tagName);
        this.lookupTagId(promises, values, key, tagName);
      } else {
        this.handleInvalidTag(values, key, tagName);
      }
    });
  
    return { uniqueArray, currentValues, values, promises };
  },
  sanitizeTag(tag) {
    return this.$tagSanitize(tag);
  },
  
  isValidTag(tagName, currentValues) {
    // Check for invalid characters using the same logic as the validation system
    const hasInvalidChars = Misc.handleInvalidTagValue(tagName).length > 0;
    
    return (
      !Misc.validateTagSize(tagName, 0) &&
      Misc.validateTagSize(tagName, 30) &&
      !currentValues.some(v => v.label.toLowerCase() === tagName.toLowerCase()) &&
      !hasInvalidChars &&
      !/^[\p{L}\p{N}]$/u.test(tagName) // Single character check
    );
  },


  capitalizeWords(str) {
    const conjunctionMap = this.conjunctions?.reduce((map, phrase) => {
      const placeholder = phrase.replace(/\s+/g, '-'); // Replace spaces with dashes as placeholders
      map[placeholder] = phrase; // Map placeholder back to the original phrase
      return map;
    }, {});

    let tagWithPlaceholders = str;
    Object.keys(conjunctionMap).forEach(placeholder => {
      const phrase = conjunctionMap[placeholder];
      const regex = new RegExp(`\\b${phrase}\\b`, 'gi'); // Match whole phrase case-insensitively
      tagWithPlaceholders = tagWithPlaceholders.replace(regex, placeholder);
    });

    let words = tagWithPlaceholders.split(' ').map((word, index) => {
      // Restore conjunctions from placeholders
      const originalWord = conjunctionMap[word] || word;
      if (index === 0 && Object.values(conjunctionMap).includes(originalWord.toLowerCase())) {
        return originalWord.toLowerCase();
      }
      // Capitalize the first word and non-conjunction words
      if (index === 0 || !Object.values(conjunctionMap).includes(originalWord.toLowerCase())) {
        return originalWord.charAt(0).toUpperCase() + originalWord.slice(1);
      }

      // Keep conjunctions lowercase
      return originalWord.toLowerCase();
    }).join(' ');    
    return words;
  },

  addTag(values, key, tagName) {
    const invalidWordsMap = {
      fontFormat: this.fontFormats,
      famousTypeDesigner: this.famousTypeDesigner,
      typefaces: this.typefaces,
      foundryName: this.foundryName,
      tautology: this.tautology,
    }
    const validatedTagName = this.validateTags(tagName, invalidWordsMap);
    const finalTagName = typeof validatedTagName === 'string' ? this.validateTags(validatedTagName, invalidWordsMap) : validatedTagName;
    this.handleInvalidTag(values, key, finalTagName)
    this.enteredTag = this.minTagLength ? finalTagName : this.enteredTag;
    tagName = tagName.trim().replace(/\s+/g, ' ');
    if (typeof finalTagName === 'string' && tagName.length > finalTagName.length) {
      Toaster.notifyError(this.$t('pages.submissions.components.descriptions.tags.removed_stop_words'));
    }
    if (finalTagName) {
      values[key] = {
        label: this.capitalizeWords(finalTagName),
        value: '0'
      }
    } else if (this.inValidTag) {
      return ;
    } else {
      values[key] = {
        label: this.capitalizeWords(tagName),
        value: '0'
      }
    }
  },

  lookupTagId(promises, values, key, tagName) {
      this.$store.dispatch('tags/get', { name: Misc.strToLowerCase(tagName) }).then(response => {
        const collection = response.data?.collection;
        if (Array.isArray(collection)) {
          const foundTag = collection.find(t => Misc.strToLowerCase(t.name) === Misc.strToLowerCase(tagName)) || {};
          if (Object.keys(foundTag).length > 0) {
            values[key].value = foundTag.id;
            values[key].label = foundTag.name.charAt(0).toUpperCase() + foundTag.name.slice(1);
          }
        }
      })
  },

  handleInvalidTag(values, key, tagName) {
    tagName = tagName.replace(/[\u2002-\u200B]/g, ' ')
    
    // Use the same validation logic as Misc.handleInvalidTagValue for consistency
    const invalidChars = Misc.handleInvalidTagValue(tagName);
    if (invalidChars.length > 0) {
      this.specialCharacters = [...new Set(invalidChars)];
      this.tagsError = true;
      this.inValidChar = true;
    } else if (/^[\p{L}\p{N}]$/u.test(tagName)) {
      this.tagsError = true;
      this.minTagLength = true;
    }

    if (!Misc.validateTagSize(tagName, 30)) {
      this.isValidTagLength = true;
    }
  },
  shouldReturnEarly(tags) {
    const currentValues = this.tags.values || []
    const currentTags = currentValues.map(v => v.label.toLowerCase()) || []
    const combinedArray = this.alltype.flat().map(tag => tag.toLowerCase());
    const uniqueArray = [...new Set(combinedArray)];
    const allTags = Array.from(new Set([...currentTags, ...uniqueArray]));
    const aiTagCount = this.visibleAiTags?.length || 0

    if ((allTags.length + aiTagCount) >= this.tagsLength) {
      this.maxTagsReached = true;
      return true;
    }
    
    const bannedWordInfo = Misc.strikeThroughBadWords(allTags.join(' '), this.$store, 'fou-select', this.$route.name, this.submission.foundry?.foundryId, [], [], true,allTags);
    this.warningWords = bannedWordInfo.warningWordMatches
    this.warningWords = this.warningWords?.map(warningWord => {
      return warningWord
        .split(' ')
        .filter(word => !this.removeWords.includes(word.toLowerCase()))
        .join(' ');
    }).filter(warningWord => warningWord.trim() !== '');
    this.warningWordForTag = bannedWordInfo.warningWordExist;
    
    if ((allTags.length + aiTagCount) > this.tagsLength) {
      this.maxTagsReached = true;
      return true;
    } else {
      this.maxTagsReached = false;
      return false;
    }
  },
  initializeTags(tags) {
    this.alltype = [];
    if (tags.trim() !== '' && !this.flag) {
      this.alltype.push(tags.trim());
    }
  },
  getCount(word) {
    this.searchCount = word.length > 0 ? word.length : 0
  },
  emptyField() {
    if(!this.bannedWordForTag && !this.inValidTag && !this.isValidTagLength && !this.minTagLength && !this.inValidChar) {
      this.enteredTag = '';
      this.$emit('update:modelValue', '');
      this.searchCount = 0;
      
      // Call removeErrors to clear validation errors when field is emptied
      this.removeErrors();
    }
  },
  removeErrors() {
    if (this.enteredTag === '') {
      this.isValidTagLength = false;
      this.inValidTag = false;
      this.minTagLength = false;
      this.inValidChar = false;
      this.bannedWordForTag = false;
      
      // Only re-validate if we have tags and we're not already validating (prevent infinite loops)
      if (this.tags.values?.length > 0 && !this._isValidating) {
        this._isValidating = true;
        this.$nextTick(() => {
          this.validate();
          this._isValidating = false;
        });
      }
    }
  },
  tagsPush(event) {
    let tags = event.target.value.trim();
    tags = tags?.replace(/[\u2002-\u200B\u205F\u3000\u2028\u202F]/g, ' ').replace(/\s+/g, ' ');
    if(tags.trim() === '') {
      return Promise.resolve();
    }

    const currentTagCount = this.getCurrentTagCount();
    if (currentTagCount >= this.tagsLength) {
      this.enforceTagLimit();
      return Promise.resolve();
    }

    this.updatedDescription = tags;

    const existingTags = [
      ...(this.tags.values || []).map(tag => ({
        id: tag.value,
        name: tag.label
      })),
      ...(this.visibleAiTags || []).map(tag => ({
        id: 'AI_GENERATED',
        name: tag.name
      }))
    ];

    const { processTagsInput } = useSubmissionUtils();

    const validationResult = processTagsInput(
      tags,
      existingTags,
      this.warningWords || [],
      this.$route,
      this.submission.foundry?.foundryId,
      this.getCurrentTagCount(),
      'fou-select',
      this.$store
    );

    // Handle validation errors using existing component logic
    this.handleValidationErrors(validationResult.errors);
    
    if (validationResult.hasErrors) {
      return Promise.resolve();
    }

    // Update warning words from validation result
    this.warningWords = validationResult.warningWords;
    this.warningWordForTag = validationResult.warningWords.length > 0;
    //Continue with component-specific logic for processed tags
    this.enteredTag = '';
    this.$emit('update:modelValue', '');
    this.searchCount = 0;
    return new Promise(resolve => {
      this.processValidatedTags(validationResult.processedTags, resolve);
    });
  },

  handleValidationErrors(errors) {
    this.isValidTagLength = errors.arrayOfMaxCharacterTag.length > 0;
    this.inValidTag = errors.seoErrorsCheck;
    this.minTagLength = errors.singleCharacterCheck;
    this.inValidChar = errors.arrayOfInvalidCharacterTag.length > 0;
    this.maxTagsReached = errors.maxTagCheck;
    this.bannedWordForTag = errors.arrayOfBannedWordsTag.length > 0;

    
    // Set special characters for error display using the exact same logic as Misc.handleInvalidTagValue
    this.specialCharacters = [];
    if (errors.arrayOfInvalidCharacterTag.length > 0) {
      errors.arrayOfInvalidCharacterTag.forEach(tag => {
        // Use the exact same regex pattern as Misc.handleInvalidTagValue for consistency
        const chars = Misc.handleInvalidTagValue(tag);
        if (chars && chars.length > 0) {
          this.specialCharacters.push(...chars);
        }
      });
      this.specialCharacters = [...new Set(this.specialCharacters)];
    }
  },

  async processValidatedTags(processedTags, resolve) {
    const currentValues = this.tags.values || [];
    const values = [];
    const promises = [];

    // Process each validated tag with component-specific logic
    for (let i = 0; i < processedTags.length; i++) {
      const tag = processedTags[i];
      const key = values.length;
      
      // Apply component-specific processing (stop words, conjunctions, etc.)
      const processedTagName = this.applyComponentSpecificProcessing(tag.name);
      
      if (processedTagName) {
        values[key] = {
          label: this.capitalizeWords(processedTagName),
          value: '0'
        };
        
        // Lookup existing tag ID from API
        await this.lookupTagId(promises, values, key, processedTagName);
      }
    }

    // Update tags with new values
    if (values.length > 0) {
      const uniqueTags = [...currentValues, ...values];
      const finalTags = Misc.removeDuplicateObjOnKey(uniqueTags, item => item.label.toLowerCase());
      this.updateTagsValues(finalTags);
      this.emitTagsCjaEvent(LINK_NAMES.NEW_USER_TAG_GENERATED)
    }
    
    resolve();
  },

  applyComponentSpecificProcessing(tagName) {
    // Apply existing component-specific validation and processing
    const invalidWordsMap = {
      fontFormat: this.fontFormats,
      famousTypeDesigner: this.famousTypeDesigner,
      typefaces: this.typefaces,
      foundryName: this.foundryName,
      tautology: this.tautology,
    };
    
    const validatedTagName = this.validateTags(tagName, invalidWordsMap);
    const finalTagName = typeof validatedTagName === 'string' ? 
      this.validateTags(validatedTagName, invalidWordsMap) : validatedTagName;
    
    if (typeof finalTagName === 'string' && tagName.length > finalTagName.length) {
      Toaster.notifyError(this.$t('pages.submissions.components.descriptions.tags.removed_stop_words'));
    }
    
    return finalTagName || tagName;
  },
  getApiTags() {
    return this.tags.getApiValues({
      value: 'id',
      name: 'name',
      multiple: true
    })
  },

  getCurrentTagCount() {
    return (this.tags?.values?.length || 0) + (this.visibleAiTags?.length || 0)
  },

  isAtTagLimit() {
    return this.getCurrentTagCount() >= this.tagsLength
  },

  isOverTagLimit() {
    return this.getCurrentTagCount() > this.tagsLength
  },

  isInputDisabled() {
    return this.isAtTagLimit()
  },

  removeAiTag(tagName) {
    const newExcluded = [...(this.excludedAiTags || [])]
    if (!newExcluded.some(e => e.toLowerCase() === tagName.toLowerCase())) {
      newExcluded.push(tagName)
    }
    this.excludedAiTags = newExcluded
    this.commitChange()
  },

  restoreAiTags() {
    this.emitTagsCjaEvent(LINK_NAMES.ADD_SUGGESTED_TAGS)
    this.excludedAiTags = []
    this.isSuggestBannerDismissed = false
    this.commitChange()
  },

  dismissSuggestBanner() {
    this.isSuggestBannerDismissed = true
  },

  clearAllTags() {
    this.emitTagsCjaEvent(LINK_NAMES.CLEAR_ALL_TAGS)
    const aiTagNames = (this.visibleAiTags || []).map(tag => tag.name)
    if (aiTagNames.length > 0) {
      const newExcluded = [...(this.excludedAiTags || [])]
      aiTagNames.forEach(name => {
        if (!newExcluded.some(e => e.toLowerCase() === name.toLowerCase())) {
          newExcluded.push(name)
        }
      })
      this.excludedAiTags = newExcluded
    }

    this.tags.values = []
    this.warningWords = []
    this.warningWordForTag = false
    this.bannedWordForTag = false
    this.inValidTag = false
    this.isValidTagLength = false
    this.minTagLength = false
    this.inValidChar = false
    this.updateTagsValues(this.tags.values)
    this.validate()
  },

  onSfdmsCompleted() {
    this.aiTagsInitialized = true
    const isLiveWithUpload = this.submission?.isLive && this.lastKnownBinaryCount > 0

    if (isLiveWithUpload || this.pendingReaggregation) {
      this.excludedAiTags = []
    }
    this.pendingReaggregation = false

    this.persistAiTags()
  },

  persistAiTags() {
    const aiTags = this.visibleAiTags.map(tag => ({
      id: 'AI_GENERATED',
      name: tag.name
    }))
    const userTags = (this.tags?.values || []).map(tag => ({
      id: tag.value || '0',
      name: tag.label
    }))

    const combined = [...userTags, ...aiTags]
    const deduped = combined.reduce((acc, current) => {
      const exists = acc.find(item => item.name.toLowerCase() === current.name.toLowerCase())
      if (!exists) {
        acc.push(current)
      }
      return acc
    }, [])

    this.submission.userMetaData.tags = deduped
    // Disabled automatic commit to prevent empty package drafts on live families; user's next action will trigger changes
    // this.commitChange()
  },

  dismissFeedback() {
    this.emitTagsCjaEvent(LINK_NAMES.CROSS_FEEDBACK)
    this.isFeedbackVisible = false
  }
}
